import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {Hint, Typeahead} from "react-bootstrap-typeahead";
import { FloatingLabel } from "./FloatingLabel";
import PropTypes from 'prop-types';
import { FormControl, InputGroup } from "react-bootstrap";
import { deepEquals } from "../utils/objectUtilities";
import { Validator } from "./ValidationResult";
import { makeLabel } from "../utils/generalUtils";
import TextInput from "./TextInput";
import { useTranslation } from "react-i18next";
import { useAutofillNotifier } from "../hooks/autofillHook";
import { withComponentCapabilities } from "./capabilities/componentCapabilities";
import { safeEval } from "../utils/safeEval";

const optLabel = opt => opt.label;
const optValue = opt => opt.value;
const optValueComparator = (opt, idx, v) => opt.value === v;

const CustomTypeaheadInput = ({ def, value, href, onChange, ...props}, { args }) => {
    const suggestions = useMemo(() => {
        let suggestions = def.suggestions;
        if (typeof suggestions === 'string') {
            suggestions = safeEval(suggestions)(value, args);
        }
        if (Array.isArray(suggestions) && suggestions.every(s => typeof s === 'string')) {
            suggestions = suggestions.map(s => ({ label: s, value: s }));
        }
        return suggestions ?? [];
    }, [def.suggestions, value, args])
    const handleChange = useCallback(value => {
        return onChange(value?.value ?? value?.label)
    }, [onChange]);
    return <TypeaheadInput options={suggestions} lockdown={def.lockdown} value={value} href={href ?? def.href} optLabel={optLabel} optValue={optValue} optValueComparator={optValueComparator} onChange={handleChange} {...props}></TypeaheadInput>
}


export const customCtrlDef = {
    def: (t, tools) => ({
        form: [{
            borderless: true,
            rows: [
                ...tools.labelFields(t),
                ...tools.fieldFields(t),
                [
                    { type: "table", field: "suggestions", sort: [], columns: [
                        { title: "Label", type: "text", field: "label", editable: true, required: true },
                        { title: "Value", type: "text", field: "value", editable: true, required: true }
                    ]}
                ],
                [
                    { type: "number", min: 1, step: 1, field: "maxlength", label: t('maxLength') },
                    { type: "checkbox", field: "lockdown", label: t('lockdown') }
                ],
                ...tools.commonFields(t)
            ]
        }]
    }),
    control: CustomTypeaheadInput
}

const CustomCtrl = ({ append, prepend, inputRef, referenceElementRef, value, onChange, onClear, validity, setValidity, ...inputProps }) => {
    const { t } = useTranslation();
    const validationCtrl = useRef();
    const { createAutofillProps } = useAutofillNotifier();

    useEffect(() => {
        setValidity();
    }, [setValidity, value]);

    const autofillProps = useMemo(() =>
        createAutofillProps((el, e) => {
            if (el.value !== value) {
                onChange?.({ persist: () => {}, ...e, currentTarget: el });
                return true;
            }
            return false;
        }, inputRef, referenceElementRef, validationCtrl)
    , [createAutofillProps, onChange, inputRef, referenceElementRef, validationCtrl, value]);

    const formCtrl = <div style={{ position: 'relative' }}>
        <FormControl ref={validationCtrl} isInvalid={validity === false} value={value} type="text" {...autofillProps} onChange={onChange} onInvalid={() => setValidity(false)} {...inputProps} />
        {(value && onClear && !inputProps?.disabled) && <button type="button" className="position-absolute px-2" style={{ top: 0, right: 0, height: '100%', border: 'none', backgroundColor: 'transparent', zIndex: 5, cursor: 'pointer'}} onFocus={inputProps?.clearBlurTimeout} onBlur={inputProps?.handleBlur} onClick={onClear}>
            <span aria-hidden="true">x</span>
            <span className="sr-only">{t('clear')}</span>
        </button>}
    </div>

    return <Hint>
        <div className="d-flex flex-column w-100">
                {prepend || append ?
                <InputGroup>
                    <Validator value={value} ref={validationCtrl} onValidation={setValidity}>
                        <div className="d-flex align-items-stretch w-100">
                            {prepend && <InputGroup.Prepend>{prepend}</InputGroup.Prepend>}
                            <div style={{ flexGrow: 1, position: 'relative' }}>
                                {formCtrl}
                            </div>
                            {append && <InputGroup.Append>{append}</InputGroup.Append>}
                        </div>
                    </Validator>
                </InputGroup>
                : <Validator value={value} ref={validationCtrl} onValidation={setValidity}>
                    {formCtrl}
                </Validator>}
        </div>
    </Hint>
}
CustomCtrl.propTypes = {
    value: PropTypes.string,
    onClear: PropTypes.func,
    append: PropTypes.node,
    prepend: PropTypes.node,
    inputRef: PropTypes.any,
    referenceElementRef: PropTypes.any
}

/**
 * A text input control with suggestions for the entries as well as, optionally, the ability to enter an entry not in the list of options.
 * @component
 */
const TypeaheadInput = withComponentCapabilities(({ id, live, options, value, placeholder, hideLabel = false, disabled = false, readonly = false, required = false, maxLength, lockdown, optLabel, optValue, optValueComparator, optSortComparitor, onChange, prependCtrl, prependText, appendCtrl, appendText, wildcard, selectableField, href, ...props}) => {
    const ctrl = useRef();
    const prevMenuVisible = useRef(false);
    const changeDebounce = useRef();
    const blurDebounce = useRef();
    const [validity, setValidity] = useState(true);

    const internalOptions = useMemo(() => {
        let internalOptions = options.filter(opt => !selectableField || opt[selectableField]).map((s, idx) => {
            if (!optLabel && !optValue) {
                return s;
            } else {
                return {
                    id: optValue ? optValue(s, idx) : s,
                    label: optLabel ? optLabel(s, idx) : s
                }
            }
        });
        internalOptions.sort((a, b) => {
            if (optSortComparitor) {
                return optSortComparitor((optLabel && optValue) ? a?.id : a, (optLabel && optValue) ? b?.id : b);
            } else if (typeof a === 'string') {
                if (a < b) {
                    return -1;
                } else if (a > b) {
                    return 1;
                }
            }
            if (a.label < b.label) {
                return -1;
            } else if (a.label > b.label) {
                return 1;
            }
            return 0;
        });
        // deduplicate
        internalOptions = internalOptions.filter((p, idx) => typeof p === 'string' ? internalOptions.indexOf(p) === idx : internalOptions.findIndex(opt => opt.id === p.id) === idx);
        return internalOptions;
    }, [options, optLabel, optValue, optSortComparitor, selectableField]);

    const buildSelection = useCallback(v => {
        if (!v) {
            return [];
        }
        if (v && optValue && optLabel) {
            const optIndex = options.findIndex((opt, idx) => optValueComparator(opt, idx, v));
            return optIndex > -1 ? [{ id: optValue(options[optIndex], optIndex), label: optLabel(options[optIndex], optIndex) }] : [{ customOption: true, label: v }];
        }
        return [v];
    }, [optValue, optLabel, optValueComparator, options]);
    const [selection, setSelection] = useState(buildSelection(value));

    useEffect(() => {
        setSelection(buildSelection(value));
    }, [value, buildSelection]);

    const fireOnChange = useCallback(value => {
        if (changeDebounce.current) {
            clearTimeout(changeDebounce.current);
        }
        changeDebounce.current = setTimeout(() => {
            setValidity(ctrl.current?.getInput().checkValidity());
            onChange(value)
        }, 200);
    }, [onChange]);
    
    const handleBlur = useCallback(() => {
        if (blurDebounce.current) {
            clearTimeout(blurDebounce.current);
        }
        if (optValue && optLabel) {
            const valueSelection = buildSelection(value);
            const inputSelection = ctrl.current.getInput().value === '' ? [] : [internalOptions.find(opt => (opt?.label ?? opt) === ctrl.current.getInput().value) ?? { customOption: true, label: ctrl.current.getInput().value }];
            if (!deepEquals(inputSelection, valueSelection)) {
                blurDebounce.current = setTimeout(() => {
                    if (inputSelection.length > 0 && !inputSelection[0].customOption) {
                        fireOnChange(inputSelection[0]);
                    } else if ((lockdown && inputSelection.length > 0) || (required && inputSelection.length === 0)) {
                        setSelection(valueSelection);
                        fireOnChange(valueSelection.length > 0 ? valueSelection[0] : null);
                    } else {
                        fireOnChange(inputSelection.length > 0 ? inputSelection[0] : null);
                    }
                }, 250)
            } else {
                setValidity(ctrl.current?.getInput().checkValidity());
            }
        } else {
            const inputValue = ctrl.current.getInput().value === '' ? null : ctrl.current.getInput().value;
            if (value !== inputValue) {
                blurDebounce.current = setTimeout(() => {
                    if (inputValue && options.some(o => o === inputValue)) {
                        fireOnChange(inputValue)
                    } else if ((lockdown && inputValue && options.every(o => o !== inputValue)) || (required && !inputValue)) {
                        setSelection(buildSelection(value));
                        fireOnChange(value);
                    } else {
                        fireOnChange(inputValue);
                    }
                }, 250);
            } else {
                setValidity(ctrl.current?.getInput().checkValidity());
            }
        }
    }, [optLabel, optValue, options, required,  internalOptions, fireOnChange, lockdown, value, buildSelection]);

    const handleSelection = useCallback((selection) => setSelection(selection), []);

    const handleMenuToggle = useCallback((visible) => {
        // detect the menu closing
        if (prevMenuVisible.current !== visible && !visible) {
            handleBlur();
        }
        prevMenuVisible.current = visible;
    }, [handleBlur]);

    const clearBlurTimeout = useCallback(() => {
        if (blurDebounce.current) {
            clearTimeout(blurDebounce.current);
            blurDebounce.current = undefined;
        }
        if (changeDebounce.current) {
            clearTimeout(changeDebounce.current);
            changeDebounce.current = undefined;
        }
    }, []);
    
    if (prependCtrl && prependText) {
        throw new Error("Cannot specify both prependCtrl and prependText");
    }

    if (appendCtrl && appendText) {
        throw new Error("Cannot specify both appendCtrl and appendText");
    }

    let prepend = null;
    if (prependCtrl || prependText) {
        prepend = prependCtrl ?? <InputGroup.Text>{prependText}</InputGroup.Text>
    }

    let append = null;
    if (appendCtrl || appendText) {
        append = appendCtrl ?? <InputGroup.Text>{appendText}</InputGroup.Text>
    }

    if (readonly) {
        return <TextInput value={selection?.[0]?.label ?? value} placeholder={makeLabel(placeholder, required)} hideLabel={hideLabel} readonly={true} required={required} prependCtrl={prependCtrl} prependText={prependText} appendCtrl={appendCtrl} appendText={appendText} wildcard={wildcard} href={href}></TextInput>
    }

    return <FloatingLabel hideLabel={hideLabel} placeholder={placeholder} required={required} href={href} value={selection?.length > 0 ? true : undefined}>
        <div style={{ position: 'relative' }}>
            <Typeahead id={id ?? placeholder?.replace(/\s+/g, '_')?.replace(/[^A-Za-z0-9]+/g, '')} align={document.documentElement.dir === 'rtl' ? 'right' : 'left'} 
                ref={ctrl} selected={selection} onChange={handleSelection} onInputChange={(text) => { if (live) { onChange(text) }}} 
                onMenuToggle={handleMenuToggle} flip={true} options={internalOptions} placeholder={makeLabel(placeholder, required)} 
                disabled={disabled} inputProps={{id, required, maxLength}} positionFixed={true} allowNew={!lockdown} onBlur={handleBlur} onFocus={clearBlurTimeout}
                {...props} renderInput={({ inputRef, referenceElementRef, ...inputProps }) => <CustomCtrl validity={validity} setValidity={setValidity} append={append} prepend={prepend} inputRef={inputRef} clearBlurTimeout={clearBlurTimeout} handleBlur={handleBlur} referenceElementRef={referenceElementRef} onClear={() => { handleSelection([]); if (!required) { ctrl.current.getInput().value = ''; handleBlur(); } }} {...inputProps}></CustomCtrl>}>
            </Typeahead>
        </div>
    </FloatingLabel>
}, {

});

TypeaheadInput.propTypes = {
    /**
     * An array of options to be used to populate the suggestions shown by the typeahead
     */
    options: PropTypes.arrayOf(PropTypes.any).isRequired,

    /**
     * The value to be displayed by the control
     */
    value: PropTypes.any,

    /**
     * Set to true for every key stroke to call the onChange function
     */
    live: PropTypes.bool,

    /**
     * Restrict users from entering new values which are not in the list of supplied options
     */
    lockdown: PropTypes.bool,

    /**
     * A function which takes an option and its index in the options list and returns the label for the
     * option which should be shown in the typeahead suggestions
     * ```js
     * (opt, idx) => ...
     * ```
     * If not supplied the option is taken directly and so should be a primitive with a sensible toString
     */
    optLabel: PropTypes.func,

    /**
     * A function which takes an option and its index in the otpions list and returns the value for the
     * option which should be returned to the containing component by the onChange Method
     * ```js
     * (opt, idx) => ...
     * ```
     * If not supplied the option is taken directly and so should be a primitive with a sensible toString
     */
    optValue: PropTypes.func,

    /**
     * A function which takes an option, it's index in the options list, and a value (as passed to the control) and returns true if the
     * option matches the value
     * ```js
     * (opt, idx, value) => ...
     * ```
     */
    optValueComparator: PropTypes.func,

    /**
     * A function which takes two options and returns 0, -1 or 1 for equal, less than or greater than
     * which is used to sort options for display
     * ```js
     * (a, b) => ...
     * ```
     * If not supplied the options are compared either by label or by value
     */
    optSortComparitor: PropTypes.func,

    /**
     * Function to call when the value has been changed
     */
    onChange: PropTypes.func.isRequired,

    /**
     * The label for the control
     */
    placeholder: PropTypes.string.isRequired,

    /**
     * The control must have a value for the from in which it is contained to validate
     */
    required: PropTypes.bool,

    /**
     * Force the input to be read-only
     */
    disabled: PropTypes.bool,

    /**
     * Collapses the floating label used for spacing
     */
    hideLabel: PropTypes.bool,
    
    /**
     * The maximum number of characters that can be entered into this control
     */
    maxLength: PropTypes.number,

    /**
     * A control to prepend to the beginning of the text input. It is invalid to specify both prependCtrl and prependText
     */
    prependCtrl: PropTypes.node,

    /**
     * Text or icon to prepend to the beginning of the text input. It is invalid to specify both prependCtrl and prependText
     */
    prependText: PropTypes.node,

    /**
     * A control to append to the end of the text input. It is invalid to specify both appendCtrl and appendText
     */
    appendCtrl: PropTypes.node,

    /**
    * Text or icon to append to the end of the text input. It is invalid to specify both appendCtrl and appendText
    */
    appendText: PropTypes.node,

    /**
    * The field to check if an option is to be rendered 
    */
    selectableField: PropTypes.string,
    
    /**
     * An option navigation URL to be followed if the label text is clicked
     */
    href: PropTypes.string,
}

export default TypeaheadInput;